import { Tooltip as AntTooltip, Empty, Table } from 'antd';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const STATUS_LABELS = {
  A: 'Abandoned',
  C: 'Completed',
};

const RISK_LABELS = {
  high: 'High',
  low: 'Low',
  unknown: 'Unknown',
  H: 'High',
  L: 'Low',
  U: 'Unknown',
};

const STATUS_COLORS = [
  '#00ababD9',
  '#E16408D9',
  '#4D6370F2',
  '#7A8A9AF2',
  '#8E44ADF2',
];

const ATTEMPT_COLORS = {
  attempt1: '#D4742EF2',
  attempt2: '#2A9D9DF2',
  attempt3: '#4D6370F2',
};

const getStatusLabel = status => {
  if (!status) return 'Unknown';
  return STATUS_LABELS[status] || `Status ${status}`;
};

const getRiskLabel = riskLevel => {
  if (!riskLevel) return 'Unknown';
  return RISK_LABELS[riskLevel] || `Risk ${riskLevel}`;
};

const formatPercentage = value => `${Number(value || 0).toFixed(2)}%`;

function useAttemptsData(attempts) {
  return useMemo(() => {
    const empty = {
      tableData: [],
      statusByRiskChartData: [],
      attemptsByRiskChartData: [],
      statusPieData: [],
      statusKeys: [],
      isEmpty: true,
    };

    if (!Array.isArray(attempts) || attempts.length === 0) return empty;

    const statusLabelsSet = new Set();
    const riskStatusTotals = {};
    const riskAttemptTotals = {};
    const statusTotals = {};
    const tableData = [];

    attempts.forEach((item, index) => {
      const statusLabel = getStatusLabel(item.status);
      const riskLabel = getRiskLabel(item.risk_level || item.risk);
      const total = item.total || 0;
      const attempt1 = item.attempt_1 || {};
      const attempt2 = item.attempt_2 || {};
      const attempt3 = item.attempt_3 || {};

      statusLabelsSet.add(statusLabel);

      tableData.push({
        key: `${index}-${item.status}-${item.risk_level || item.risk}`,
        status: statusLabel,
        risk: riskLabel,
        attempt1Percentage: attempt1.percentage || 0,
        attempt1Count: attempt1.count || 0,
        attempt2Percentage: attempt2.percentage || 0,
        attempt2Count: attempt2.count || 0,
        attempt3Percentage: attempt3.percentage || 0,
        attempt3Count: attempt3.count || 0,
      });

      if (!riskStatusTotals[riskLabel]) riskStatusTotals[riskLabel] = {};
      riskStatusTotals[riskLabel][statusLabel] =
        (riskStatusTotals[riskLabel][statusLabel] || 0) + total;

      if (!riskAttemptTotals[riskLabel]) {
        riskAttemptTotals[riskLabel] = {
          attempt1: 0,
          attempt2: 0,
          attempt3: 0,
        };
      }
      riskAttemptTotals[riskLabel].attempt1 += attempt1.count || 0;
      riskAttemptTotals[riskLabel].attempt2 += attempt2.count || 0;
      riskAttemptTotals[riskLabel].attempt3 += attempt3.count || 0;

      statusTotals[statusLabel] = (statusTotals[statusLabel] || 0) + total;
    });

    const statusKeys = Array.from(statusLabelsSet);

    const statusByRiskChartData = Object.entries(riskStatusTotals).map(
      ([risk, statuses]) => ({ risk, ...statuses }),
    );

    const attemptsByRiskChartData = Object.entries(riskAttemptTotals).map(
      ([risk, counts]) => ({ risk, ...counts }),
    );

    const statusPieData = Object.entries(statusTotals).map(([name, value]) => ({
      name,
      value,
    }));

    return {
      tableData,
      statusByRiskChartData,
      attemptsByRiskChartData,
      statusPieData,
      statusKeys,
      isEmpty: false,
    };
  }, [attempts]);
}

function StatusByRiskChart({ data, statusKeys }) {
  const intl = useIntl();

  if (data.length === 0 || statusKeys.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <Empty
          description={intl.formatMessage({
            id: 'page.billing.attempts.noStatusData',
          })}
        />
      </div>
    );
  }

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
    >
      <BarChart
        data={data}
        margin={{ top: 20, right: 20, left: 0, bottom: 10 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="risk" />
        <YAxis allowDecimals={false} />
        <Tooltip
          formatter={value => [
            value,
            intl.formatMessage({ id: 'page.billing.attempts.totalAttempts' }),
          ]}
        />
        <Legend />
        {statusKeys.map((statusKey, index) => (
          <Bar
            key={statusKey}
            dataKey={statusKey}
            fill={STATUS_COLORS[index % STATUS_COLORS.length]}
            stackId="statusStack"
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function AttemptsByRiskChart({ data }) {
  const intl = useIntl();

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <Empty
          description={intl.formatMessage({
            id: 'page.billing.attempts.noAttemptsData',
          })}
        />
      </div>
    );
  }

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
    >
      <BarChart
        data={data}
        margin={{ top: 20, right: 20, left: 0, bottom: 10 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="risk" />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Legend />
        <Bar
          dataKey="attempt1"
          name={intl.formatMessage({
            id: 'page.billing.attempts.column.attempt1',
          })}
          fill={ATTEMPT_COLORS.attempt1}
          isAnimationActive={false}
        />
        <Bar
          dataKey="attempt2"
          name={intl.formatMessage({
            id: 'page.billing.attempts.column.attempt2',
          })}
          fill={ATTEMPT_COLORS.attempt2}
          isAnimationActive={false}
        />
        <Bar
          dataKey="attempt3"
          name={intl.formatMessage({
            id: 'page.billing.attempts.column.attempt3',
          })}
          fill={ATTEMPT_COLORS.attempt3}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

function StatusDistributionChart({ data }) {
  const intl = useIntl();

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <Empty
          description={intl.formatMessage({
            id: 'page.billing.attempts.noDistributionData',
          })}
        />
      </div>
    );
  }

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
    >
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={95}
          label
        >
          {data.map((entry, index) => (
            <Cell
              key={entry.name}
              fill={STATUS_COLORS[index % STATUS_COLORS.length]}
            />
          ))}
        </Pie>
        <Tooltip
          formatter={value => [
            value,
            intl.formatMessage({ id: 'page.billing.attempts.totalAttempts' }),
          ]}
        />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}

function AttemptsTable({ data }) {
  const intl = useIntl();

  const renderPercentageWithTooltip = countField => (value, record) => (
    <AntTooltip
      title={`${intl.formatMessage({ id: 'page.billing.attempts.totalAttempts' })}: ${record[countField]}`}
    >
      <span>{formatPercentage(value)}</span>
    </AntTooltip>
  );

  const columns = [
    {
      title: <FormattedMessage id="page.billing.attempts.column.status" />,
      dataIndex: 'status',
      key: 'status',
      width: 180,
    },
    {
      title: <FormattedMessage id="page.billing.attempts.column.risk" />,
      dataIndex: 'risk',
      key: 'risk',
      width: 150,
    },
    {
      title: <FormattedMessage id="page.billing.attempts.column.attempt1" />,
      dataIndex: 'attempt1Percentage',
      key: 'attempt1Percentage',
      width: 130,
      render: renderPercentageWithTooltip('attempt1Count'),
    },
    {
      title: <FormattedMessage id="page.billing.attempts.column.attempt2" />,
      dataIndex: 'attempt2Percentage',
      key: 'attempt2Percentage',
      width: 130,
      render: renderPercentageWithTooltip('attempt2Count'),
    },
    {
      title: <FormattedMessage id="page.billing.attempts.column.attempt3" />,
      dataIndex: 'attempt3Percentage',
      key: 'attempt3Percentage',
      width: 130,
      render: renderPercentageWithTooltip('attempt3Count'),
    },
  ];

  return (
    <Table
      rowKey="key"
      dataSource={data}
      columns={columns}
      pagination={false}
      scroll={{ y: 320 }}
    />
  );
}

function BillingAttemptsByRiskSection({ attempts = [] }) {
  const intl = useIntl();
  const {
    tableData,
    statusByRiskChartData,
    attemptsByRiskChartData,
    statusPieData,
    statusKeys,
    isEmpty,
  } = useAttemptsData(attempts);

  if (isEmpty) {
    return (
      <div className="border rounded-lg p-5">
        <h3 className="text-gray-700 mb-4">
          <FormattedMessage id="page.billing.attempts.title" />
        </h3>
        <Empty
          description={intl.formatMessage({
            id: 'page.billing.attempts.noData',
          })}
        />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col lg:flex-row mb-5 gap-5">
        <div
          className="border rounded-lg lg:w-1/2"
          style={{ height: '450px' }}
        >
          <StatusByRiskChart
            data={statusByRiskChartData}
            statusKeys={statusKeys}
          />
        </div>
        <div
          className="border rounded-lg lg:w-1/2"
          style={{ height: '450px' }}
        >
          <AttemptsByRiskChart data={attemptsByRiskChartData} />
        </div>
        <div
          className="border rounded-lg lg:w-1/2"
          style={{ height: '450px' }}
        >
          <StatusDistributionChart data={statusPieData} />
        </div>
      </div>
      <div className="flex flex-col lg:flex-row mb-5 gap-5">
        <div className="border rounded-lg">
          <AttemptsTable data={tableData} />
        </div>
      </div>
    </>
  );
}

export default BillingAttemptsByRiskSection;
